import React from 'react';

// API/Components
import {
	ANALYSIS_TYPE,
	AnalysisResultToStateColor,
	isEmpty,
	evaluateRiskIndex,
	formatDateTime,
	buildMeasurementText
} from './enums';

// Material UI
import Backdrop from '@mui/material/Backdrop';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';

// set the width of the left column in the table
const leftColumnWidth = '45%';

export default function SystemMeasurementSummary({ systemAnalysisData, loading = false }) {

	// Default table data structure
	let tableData = {
		measurementDate: '--/--/---- --:--:--',
		numStrings: '-',
		numUnitsPerString: '--',
		systemVoltage: { measText: '--.- V', outText: '' },
		voltageColor: 'inherit',
		systemCurrent: { measText: '--.- A', outText: '' },
		currentColor: 'inherit',
		ambientTemp: { measText: '--.- °C', outText: '' },
		ambientTempColor: 'inherit',
		riskIndex: '--.-'
	};

	if (systemAnalysisData !== undefined && !isEmpty(systemAnalysisData)) {
		const measurementDate = formatDateTime(systemAnalysisData.analysis.measurement_datetime);
		const voltage = systemAnalysisData.analysis.voltage;
		const current = systemAnalysisData.analysis.current;
		const ambientTemp = systemAnalysisData.analysis.ambient_temp;
		const riskIndex = evaluateRiskIndex(systemAnalysisData.analysis.risk_index.risk_index, systemAnalysisData.analysis.risk_index.risk_flags);

		const voltageText = buildMeasurementText(voltage.measurement, voltage.analysis_valid, voltage.analysis_out, voltage.analysis_result, ANALYSIS_TYPE.VOLTAGE, 1, 2, '---.-');
		const currentText = buildMeasurementText(current.measurement, current.analysis_valid, current.analysis_out, current.analysis_result, ANALYSIS_TYPE.CURRENT);
		const ambientTempText = buildMeasurementText(ambientTemp.measurement, ambientTemp.analysis_valid, ambientTemp.analysis_out, ambientTemp.analysis_result, ANALYSIS_TYPE.TEMPERATURE);
		
		tableData = {
			measurementDate: measurementDate,
			numStrings: systemAnalysisData.info.num_strings,
			numUnitsPerString: systemAnalysisData.info.num_units_per_string,
			systemVoltage: voltageText,
			voltageColor: AnalysisResultToStateColor(voltage.analysis_result) + 'aa',
			systemCurrent: currentText,
			currentColor: AnalysisResultToStateColor(current.analysis_result) + 'aa',
			ambientTemp: ambientTempText,
			ambientTempColor: AnalysisResultToStateColor(ambientTemp.analysis_result) + 'aa',
			riskIndex: riskIndex
		};
	}

	return (
		<Card style={{ border: '1px solid #D8D7D6', borderRadius: '5px', boxShadow: '2px 2px 4px gray', px: 0 }}>
			<Box sx={{ backgroundColor: 'lightgray', p: 1 }}>
				<Typography sx={{ textAlign: 'center', fontFamily: 'Saira,sans-serif', fontWeight: 'bold' }}>
					System Measurement Summary
				</Typography>
			</Box>
			<CardContent style={{ padding: 0, position: 'relative' }}>
				{loading && (
					<Backdrop
						sx={{
							color: '#fff',
							zIndex: (theme) => theme.zIndex.drawer + 1,
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							borderRadius: 'inherit',
							backgroundColor: 'rgba(0,0,0,0.5)'
						}}
						open={true}
					>
						<CircularProgress color="inherit" />
					</Backdrop>
				)}
				<TableContainer style={{ width: '100%' }}>
					<Table>
						<TableBody>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>Measurement Date</TableCell>
								<TableCell sx={{ p: 1 }}>{tableData.measurementDate}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>System Size</TableCell>
								<TableCell sx={{ p: 1 }}>{tableData.numStrings} Strings of {tableData.numUnitsPerString} Units</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>System Voltage</TableCell>
								<TableCell sx={{ p: 1, backgroundColor: tableData.voltageColor }}>
									{tableData.systemVoltage.measText}DC {tableData.systemVoltage.outText}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>System Current</TableCell>
								<TableCell sx={{ p: 1, backgroundColor: tableData.currentColor }}>
									{tableData.systemCurrent.measText} {tableData.systemCurrent.outText}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>Ambient Temp</TableCell>
								<TableCell sx={{ p: 1, backgroundColor: tableData.ambientTempColor }}>
									{tableData.ambientTemp.measText} {tableData.ambientTemp.outText}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>Risk Index</TableCell>
								<TableCell sx={{ p: 1 }}>{tableData.riskIndex}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</CardContent>
		</Card>
	);
}
